import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";

import { uploadImage, getMyImages, getImageUrlByFilename } from "../../api/subjects";

export default function ImagePickerDialog({ open, onClose, onPick }) {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [images, setImages] = useState([]);
    const [desc, setDesc] = useState("");

    async function load() {
        setLoading(true);
        try {
            const data = await getMyImages();
            setImages(Array.isArray(data) ? data : []);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (open) load();
    }, [open]);

    const sorted = useMemo(() => {
        return [...images].sort((a, b) =>
            String(b.uploadedAt || "").localeCompare(String(a.uploadedAt || ""))
        );
    }, [images]);

    async function onUploadFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const res = await uploadImage(file, desc);
            const filename = res.filename;
            const url = getImageUrlByFilename(filename);

            onPick?.({
                url,
                filename,
                originalFilename: res.originalFilename,
            });

            await load();
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    }

    function pickFromList(img) {
        const filename = img.filename;
        const url = getImageUrlByFilename(filename);

        onPick?.({
            url,
            filename,
            originalFilename: img.originalFilename,
            description: img.description,
        });
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Зображення</DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                    >
                        <TextField
                            label="Підпис / description (опційно)"
                            value={desc}
                            onChange={(e) => setDesc(e.target.value)}
                            fullWidth
                        />

                        <Button variant="contained" component="label" disabled={uploading}>
                            {uploading ? <CircularProgress size={18} /> : "Завантажити файл"}
                            <input hidden type="file" accept="image/*" onChange={onUploadFile} />
                        </Button>
                    </Stack>

                    {loading ? (
                        <Stack direction="row" spacing={2} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography color="text.secondary">Завантаження...</Typography>
                        </Stack>
                    ) : sorted.length === 0 ? (
                        <Typography color="text.secondary">Ще немає зображень</Typography>
                    ) : (
                        <Box
                            sx={{
                                maxHeight: 420,
                                overflowY: "auto",
                                pr: 1,
                            }}
                        >
                            <Grid container spacing={1.5}>
                                {sorted.map((img) => {
                                    const url = getImageUrlByFilename(img.filename);

                                    return (
                                        <Grid item xs={6} sm={4} md={3} lg={2} key={img._id || img.filename}>
                                            <Box
                                                onClick={() => pickFromList(img)}
                                                sx={{
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 2,
                                                    overflow: "hidden",
                                                    cursor: "pointer",
                                                    bgcolor: "background.paper",
                                                    boxShadow: 1,
                                                    transition: "transform 120ms ease, box-shadow 120ms ease",
                                                    "&:hover": {
                                                        transform: "translateY(-2px)",
                                                        boxShadow: 3,
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        height: 110,
                                                        bgcolor: "grey.100",
                                                    }}
                                                >
                                                    <img
                                                        src={url}
                                                        alt={img.originalFilename || img.filename}
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                            display: "block",
                                                        }}
                                                        loading="lazy"
                                                    />
                                                </Box>
                                                <Box sx={{ p: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        noWrap
                                                        title={img.originalFilename || img.filename}
                                                        sx={{ display: "block", fontWeight: 600 }}
                                                    >
                                                        {img.originalFilename || img.filename}
                                                    </Typography>
                                                    {!!img.fileSize && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ display: "block" }}
                                                        >
                                                            {(Number(img.fileSize) / 1024).toFixed(0)} KB
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Закрити</Button>
            </DialogActions>
        </Dialog>
    );
}
